/**
 * 将后端 VO/Entity 字段规范为前端 Store/页面使用的形状（P1 DTO 对齐）
 */
import type { Company, Contract, Project, PaymentMethod, TaskAttachment, Supplier, SupplierStatus, SupplierType, SupplierSettlementType } from '../types';

function fmtIsoDate(v: unknown): string {
  if (v == null || v === '') return '';
  const s = String(v);
  return s.length >= 10 ? s.slice(0, 10) : s;
}

function fmtDateTime(v: unknown): string {
  if (v == null || v === '') return '';
  return String(v);
}

function parseTags(raw: Record<string, unknown>): string[] {
  const ar = raw.tagsArray;
  if (Array.isArray(ar)) return ar as string[];
  const t = raw.tags;
  if (Array.isArray(t)) return t as string[];
  if (typeof t === 'string' && t.trim()) {
    try {
      const p = JSON.parse(t) as unknown;
      return Array.isArray(p) ? (p as string[]) : [];
    } catch {
      return [];
    }
  }
  return [];
}

/** 对接 CompanyVO：customerSource、createTime、tags 等 */
export function normalizeCompanyVo(raw: Record<string, unknown>): Company {
  const customerSource = (raw.customerSource ?? raw.source) as string | undefined;
  const customerLevel = (raw.customerLevel ?? raw.level) as string | undefined;
  const ct = raw.createTime ?? raw.createdAt;
  const ut = raw.updateTime ?? raw.updatedAt;
  const createdAt = fmtDateTime(ct);
  const updatedAt = fmtDateTime(ut);
  const tags = parseTags(raw);
  const base = { ...(raw as unknown as Company) };
  const id = raw.id != null ? String(raw.id) : base.id;
  const assistantTeamJson =
    raw.assistantTeamJson != null && String(raw.assistantTeamJson).trim() !== ''
      ? String(raw.assistantTeamJson)
      : base.assistantTeamJson;
  const hasInvoiceInfo =
    raw.invoiceTaxNo != null ||
    raw.invoiceAddress != null ||
    raw.invoicePhone != null ||
    raw.invoiceBankName != null ||
    raw.invoiceBankAccount != null ||
    raw.invoiceType != null;
  const invoiceInfo = hasInvoiceInfo
    ? {
        taxNo: String(raw.invoiceTaxNo ?? ''),
        companyAddress: String(raw.invoiceAddress ?? ''),
        companyPhone: String(raw.invoicePhone ?? ''),
        bankName: String(raw.invoiceBankName ?? ''),
        bankAccount: String(raw.invoiceBankAccount ?? ''),
        invoiceType: String(raw.invoiceType ?? 'special') === 'normal' ? ('normal' as const) : ('special' as const),
      }
    : base.invoiceInfo;
  return {
    ...base,
    id,
    assistantTeamJson,
    customerSource: customerSource ?? base.customerSource,
    customerLevel: customerLevel ?? base.customerLevel,
    source: customerSource ?? base.source,
    level: customerLevel ?? base.level,
    createTime: ct != null ? fmtDateTime(ct) : undefined,
    updateTime: ut != null ? fmtDateTime(ut) : undefined,
    createdAt: createdAt || base.createdAt,
    updatedAt: updatedAt || base.updatedAt,
    tags: tags.length ? tags : base.tags ?? [],
    invoiceInfo,
  };
}

function mapFinContractStatus(s: string): Contract['status'] {
  const v = s.toLowerCase();
  if (v === 'completed') return 'completed';
  if (v === 'terminated' || v === 'cancelled') return 'terminated';
  if (v === 'draft' || v === 'pending' || v === 'rejected') return 'draft';
  return 'active';
}

function mapPaymentMethod(pm: unknown): PaymentMethod {
  const s = String(pm ?? 'one_time').toLowerCase();
  if (s === 'installment' || s === 'milestone' || s === 'one_time') return s;
  return 'one_time';
}

/** fin_contract.files：JSON 数组字符串 → 合同附件列表 */
function parseContractFilesField(raw: Record<string, unknown>): TaskAttachment[] | undefined {
  const f = raw.files;
  if (f == null || f === '') return undefined;
  if (typeof f !== 'string') return undefined;
  try {
    const j = JSON.parse(f) as unknown;
    if (!Array.isArray(j)) return undefined;
    return j.map((item, i) => {
      const o = (item ?? {}) as Record<string, unknown>;
      return {
        id: String(o.id ?? `file-${i}`),
        name: String(o.name ?? '附件'),
        url: String(o.url ?? '#'),
        size: Number(o.size ?? 0),
        type: String(o.type ?? 'application/pdf'),
        uploadedAt: o.uploadedAt != null ? String(o.uploadedAt) : undefined,
      };
    });
  } catch {
    return undefined;
  }
}

/** 对接 FinContract：name、amount、signedDate、paymentType、createTime */
export function normalizeContractFinToUi(raw: Record<string, unknown>): Contract {
  const amount = Number(raw.amount ?? raw.totalAmount ?? 0);
  const signed = raw.signedDate ?? raw.signDate;
  const name = String(raw.name ?? raw.contractName ?? '');
  const net = Number(raw.netAmount ?? amount);
  const taxAmt = Number(raw.taxAmount ?? 0);
  const taxRate = Number(raw.taxRate ?? 0);
  return {
    id: String(raw.id ?? ''),
    version: raw.version == null ? undefined : Number(raw.version),
    contractNo: String(raw.contractNo ?? ''),
    contractName: name,
    name: name || undefined,
    companyId: String(raw.companyId ?? ''),
    companyName: String(raw.companyName ?? ''),
    projectId: raw.projectId != null ? String(raw.projectId) : undefined,
    projectName: raw.projectName != null ? String(raw.projectName) : undefined,
    signDate: fmtIsoDate(signed),
    startDate: fmtIsoDate(raw.startDate),
    endDate: fmtIsoDate(raw.endDate),
    totalAmount: amount,
    taxRate,
    taxAmount: taxAmt,
    netAmount: net,
    paymentMethod: mapPaymentMethod(raw.paymentType ?? raw.paymentMethod),
    status: mapFinContractStatus(String(raw.status ?? 'draft')),
    remark: raw.remark != null ? String(raw.remark) : undefined,
    owner: String(raw.owner ?? raw.signedBy ?? raw.createBy ?? ''),
    attachments: parseContractFilesField(raw),
    createdAt: fmtDateTime(raw.createTime ?? raw.createdAt),
    updatedAt: fmtDateTime(raw.updateTime ?? raw.updatedAt),
  };
}

/** 对接 ProjectVO：createTime/updateTime 与 createdAt/updatedAt 双写 */
export function normalizeProjectFromApi(row: Record<string, unknown>): Project {
  const base = { ...(row as unknown as Project) };
  const ct = row.createTime != null ? fmtDateTime(row.createTime) : base.createdAt;
  const ut = row.updateTime != null ? fmtDateTime(row.updateTime) : base.updatedAt;
  const prog = row.progress != null ? Number(row.progress) : base.progress;
  const rawContacts = Array.isArray(row.contacts) ? row.contacts : [];
  return {
    ...base,
    createdAt: ct,
    updatedAt: ut,
    createTime: row.createTime != null ? fmtDateTime(row.createTime) : undefined,
    updateTime: row.updateTime != null ? fmtDateTime(row.updateTime) : undefined,
    progress: Number.isFinite(prog) ? prog : 0,
    contacts: rawContacts
      .map((item) => {
        const contact = item as Record<string, unknown>;
        const contactId = contact.contactId != null ? String(contact.contactId) : '';
        const contactName = contact.contactName != null ? String(contact.contactName) : '';
        if (!contactId || !contactName) return null;
        return {
          id: contact.id != null ? String(contact.id) : undefined,
          contactId,
          contactName,
          role: contact.role != null ? String(contact.role) : undefined,
          phone: contact.phone != null ? String(contact.phone) : undefined,
          email: contact.email != null ? String(contact.email) : undefined,
          isPrimary: contact.isPrimary != null ? Number(contact.isPrimary) : undefined,
        };
      })
      .filter((item): item is NonNullable<typeof item> => item != null),
  };
}

/** 对接财务 SupplierVO（/fin/suppliers） */
export function normalizeSupplierFromApi(row: Record<string, unknown>): Supplier {
  const st = (row.status as string) || 'active';
  const ty = (row.type as string) || 'other';
  return {
    id: String(row.id ?? ''),
    supplierNo: String(row.supplierNo ?? ''),
    name: String(row.name ?? ''),
    shortName: row.shortName != null ? String(row.shortName) : undefined,
    type: (ty in supplierTypeSet ? ty : 'other') as SupplierType,
    category: row.category != null ? String(row.category) : undefined,
    contactPerson: row.contactPerson != null ? String(row.contactPerson) : undefined,
    contactId: row.contactId != null ? String(row.contactId) : undefined,
    contactPhone: row.contactPhone != null ? String(row.contactPhone) : undefined,
    contactEmail: row.contactEmail != null ? String(row.contactEmail) : undefined,
    address: row.address != null ? String(row.address) : undefined,
    taxNo: row.taxNo != null ? String(row.taxNo) : undefined,
    bankName: row.bankName != null ? String(row.bankName) : undefined,
    bankAccount: row.bankAccount != null ? String(row.bankAccount) : undefined,
    status: (st in supplierStatusSet ? st : 'active') as SupplierStatus,
    cooperationStartDate: fmtIsoDate(row.cooperationStartDate),
    cooperationEndDate: fmtIsoDate(row.cooperationEndDate),
    settlementType:
      row.settlementType != null && settlementSet[String(row.settlementType)]
        ? (String(row.settlementType) as SupplierSettlementType)
        : undefined,
    creditPeriod: row.creditPeriod != null ? Number(row.creditPeriod) : undefined,
    totalTransactionAmount: row.totalTransactionAmount != null ? Number(row.totalTransactionAmount) : undefined,
    totalTransactionCount: row.totalTransactionCount != null ? Number(row.totalTransactionCount) : undefined,
    lastTransactionDate: fmtIsoDate(row.lastTransactionDate),
    remark: row.remark != null ? String(row.remark) : undefined,
    owner: String(row.owner ?? ''),
    createdAt: fmtDateTime(row.createTime ?? row.createdAt),
    updatedAt: fmtDateTime(row.updateTime ?? row.updatedAt),
  };
}

const supplierTypeSet: Record<string, true> = {
  logistics: true, printing: true, consulting: true, training: true, it: true, office: true, other: true,
};
const supplierStatusSet: Record<string, true> = {
  active: true, inactive: true, pending: true, blacklist: true,
};
const settlementSet: Record<string, true> = {
  monthly: true, quarterly: true, per_project: true, one_time: true,
};
